/**
 * Two-way sync between the applied filters and the URL query string (brief §7).
 *
 * A shared view is just a link: the applied filters and the count mode are
 * encoded as query params, read once on load, and rewritten (via replaceState,
 * so it doesn't spam history) whenever they change.
 */
import { useEffect } from "react";

import type { Satellite } from "../data/types";
import { useFiltersStore, type CountMode } from "../store/filtersStore";

const SATELLITES: Satellite[] = ["sentinel-2", "landsat-8", "landsat-9", "emit"];

function readFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const sat = (params.get("sat") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is Satellite => (SATELLITES as string[]).includes(s));
  const min = Number(params.get("min"));
  const countRaw = params.get("count");
  const countMode: CountMode | undefined =
    countRaw === "extent" || countRaw === "country" ? countRaw : undefined;

  return {
    values: {
      from: params.get("from") ?? "",
      to: params.get("to") ?? "",
      satellites: sat,
      minRate: Number.isFinite(min) && min > 0 ? min : 0,
    },
    countMode,
  };
}

function writeToUrl(): void {
  const { applied, countMode } = useFiltersStore.getState();
  const params = new URLSearchParams();
  if (applied.from) params.set("from", applied.from);
  if (applied.to) params.set("to", applied.to);
  if (applied.satellites.length) params.set("sat", applied.satellites.join(","));
  if (applied.minRate > 0) params.set("min", String(applied.minRate));
  if (countMode !== "country") params.set("count", countMode);

  const query = params.toString();
  const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState(null, "", url);
}

export function useUrlFilterSync(): void {
  useEffect(() => {
    // Read once so a shared link restores state, then mirror applied → URL.
    const { values, countMode } = readFromUrl();
    useFiltersStore.getState().hydrate(values, countMode);
    writeToUrl();
    return useFiltersStore.subscribe(writeToUrl);
  }, []);
}
